// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useRef } from "react"
import { autoUpdate, flip, limitShift, shift, size, useFloating } from "@floating-ui/react-dom"
import styled from "styled-components/macro"
import { t } from "@lingui/macro"
import { DropdownHeader, Wrapper } from "features/shared/search-dropdown/styles"
import SvgIcon from "features/shared/SvgIcon"
import AddToGroupDropdownContent from "./AddToGroupDropdownContent"
import { useDropdownHandling } from "features/shared/search-dropdown/useDropdownHandling"

const StyledSvgIcon = styled(SvgIcon)`
  width: ${({ theme }) => theme.iconSizeInButton};
  height: ${({ theme }) => theme.iconSizeInButton};
`

type Props = {
  timeRowId: string,
}

const AddToGroupDropdown = ({
  timeRowId,
}: Props): React.ReactElement<Props> | null => {
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const {
    isDropdownVisible,
    setIsDropdownVisible,
  } = useDropdownHandling(dropdownRef, true)

  const {
    refs,
    floatingStyles,
  } = useFloating({
    strategy: "fixed",
    placement: "bottom-end",
    middleware: [
      size({
        apply({ availableHeight, elements }) {
          const values = [availableHeight - 20, window.innerHeight - 40]
          elements.floating.style.height = `${Math.min(...values)}px`
        },
      }),
      flip({ fallbackStrategy: "initialPlacement" }),
      shift({ limiter: limitShift() }),
    ],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true,
      }),
  })

  return (
    <Wrapper
      ref={dropdownRef}
      onClick={(event: React.MouseEvent<HTMLDivElement>): void => event.stopPropagation()}
    >
      <DropdownHeader
        ref={refs.setReference}
        isDropdownVisible={isDropdownVisible}
        title={t({ id: "label_add_to_timesheet", message: "Add to Timesheet" })}
        tabIndex={-1}
        onClick={(): void => setIsDropdownVisible(!isDropdownVisible)}
      >
        <StyledSvgIcon name="add-to-group"/>
      </DropdownHeader>
      {isDropdownVisible && (
        <AddToGroupDropdownContent
          ref={refs.setFloating}
          style={floatingStyles}
          timeRowId={timeRowId}
          setIsDropdownVisible={setIsDropdownVisible}
        />
      )}
    </Wrapper>
  )
}

export default AddToGroupDropdown
